import { useEffect, useState } from 'react';
import { invites, type PlayerInviteDto } from '../net/api';

const STATUS: Record<PlayerInviteDto['status'], string> = { open: 'Open', used_up: 'Used up', expired: 'Expired', cancelled: 'Cancelled' };

/** Account panel: invite codes for friends (15 sign-ups each, 30 days). */
export function InviteFriends() {
  const [data, setData] = useState<Awaited<ReturnType<typeof invites.mine>>>();
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string }>();
  const [friendRequest, setFriendRequest] = useState(true);
  const load = () => invites.mine().then(setData, (e: Error) => setMsg({ kind: 'err', text: e.message }));
  useEffect(() => {
    void load();
  }, []);
  if (!data) return msg ? <p className="form-error">{msg.text}</p> : null;
  const link = (code: string) => `${window.location.origin}/?invite=${code}`;
  const copy = (code: string) =>
    navigator.clipboard.writeText(link(code)).then(() => setMsg({ kind: 'ok', text: 'Invite link copied!' }), () => setMsg({ kind: 'ok', text: link(code) }));
  const act = async (fn: () => Promise<unknown>, ok?: string) => {
    try {
      await fn();
      setMsg(ok ? { kind: 'ok', text: ok } : undefined);
      await load();
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message });
    }
  };
  const shown = data.codes.filter((c) => c.status === 'open').concat(data.codes.filter((c) => c.status !== 'open').slice(0, 3));
  // Players too young to make codes (and with none from before) don't see this section.
  if (data.blocked && !data.codes.length && /aged/.test(data.blocked)) return null;
  return (
    <div className="invite-friends">
      <h3 className="mini-head">Invite friends</h3>
      <p className="muted small-print">
        Share a code with friends so they can join. Each code works for {data.usesPerCode} sign-ups and runs out after {data.days} days. You can make {data.limit} codes every {data.windowDays} days
        ({data.madeRecently} made so far). Friends still sign up the usual way.
        {data.signupClosed && ' New accounts are closed right now, so codes will work once sign-up opens.'}
      </p>
      {shown.length > 0 && (
        <ul className="invite-list">
          {shown.map((c) => (
            <li key={c.code} className={c.status === 'open' ? undefined : 'muted'}>
              <code>{c.code}</code>{' '}
              <span className="small-print">
                {c.uses}/{c.maxUses} used · {c.status === 'open' ? `until ${new Date(c.expiresAt).toLocaleDateString()}` : STATUS[c.status]}
              </span>
              {c.status !== 'cancelled' && (
                <label className="check small-print invite-fr">
                  <input
                    type="checkbox"
                    checked={c.friendRequest}
                    onChange={(e) => void act(() => invites.setFriendRequest(c.code, e.currentTarget.checked), e.currentTarget.checked ? 'Friends who join with this code will send you a friend request.' : 'No friend requests from this code.')}
                  />{' '}
                  Send me a friend request
                </label>
              )}
              {c.status === 'open' && (
                <>
                  {' '}<button className="link-btn small-print" onClick={() => void copy(c.code)}>Copy link</button>
                  {' '}<button className="link-btn small-print" onClick={() => { if (window.confirm(`Cancel code ${c.code}? It stops working straight away.`)) void act(() => invites.cancel(c.code), 'Code cancelled.'); }}>Cancel</button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {data.blocked ? (
        <p className="muted small-print">{data.blocked}</p>
      ) : (
        <div className="row-wrap">
          <label className="check small-print">
            <input type="checkbox" checked={friendRequest} onChange={(e) => setFriendRequest(e.currentTarget.checked)} /> Friends who join send me a friend request
          </label>
          <button className="btn small ghost" onClick={() => void act(() => invites.create(friendRequest), 'New code made! Copy the link and send it to a friend.')}>Make an invite code</button>
        </div>
      )}
      {msg && <p className={msg.kind === 'ok' ? 'note-ok' : 'form-error'} role="status">{msg.text}</p>}
    </div>
  );
}
